import { Component, OnDestroy, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { Subject, takeUntil } from "rxjs";
import { Dialog } from "@angular/cdk/dialog";
import { HttpErrorResponse } from "@angular/common/http";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { IconDefinition } from "@fortawesome/fontawesome-common-types";
import { faEnvelope, faEye, faPaperPlane, faSpinner } from "@fortawesome/pro-light-svg-icons";
import {
  ConsumerEmailCampaignRequest,
  ConsumerEmailCampaignResult
} from "../../../models/consumer-email-campaign.model";
import { BusinessLineOption } from "../../../models/consumer-sms-campaign.model";
import {
  CampaignAutomationSettings,
  CampaignAutomationSettingsRequest,
  CampaignCadenceMode
} from "../../../models/campaign-automation-settings.model";
import { ConsumerEmailCampaignService } from "../../../services/consumer-email-campaign.service";
import { ConsumerSmsCampaignService } from "../../../services/consumer-sms-campaign.service";
import { CampaignAutomationSettingsService } from "../../../services/campaign-automation-settings.service";
import { ConfirmationModalComponent } from "../../confirmation-modal/confirmation-modal.component";
import { ConfirmationModalData } from "../../../models/confirmation-modal-data.model";
import { ToastService } from "../../../services/toast.service";
import { StateManagementService } from "../../../services/state-management.service";

@Component({
  selector: "app-consumer-email-campaign",
  standalone: true,
  imports: [CommonModule, FormsModule, FontAwesomeModule],
  templateUrl: "./consumer-email-campaign.component.html"
})
export class ConsumerEmailCampaignComponent implements OnInit, OnDestroy {
  protected readonly faEnvelope: IconDefinition = faEnvelope;
  protected readonly faEye: IconDefinition = faEye;
  protected readonly faPaperPlane: IconDefinition = faPaperPlane;
  protected readonly faSpinner: IconDefinition = faSpinner;

  protected fromDate: string = this.today();
  protected toDate: string = this.today();
  protected readonly maxDate: string = this.today();
  protected limit: number | null = null;
  protected isPreviewing: boolean = false;
  protected isSending: boolean = false;
  protected previewResult: ConsumerEmailCampaignResult | null = null;
  protected sendResult: ConsumerEmailCampaignResult | null = null;

  protected readonly categoryOptions: { value: string; label: string }[] = [
    { value: "PERSONAL", label: "Personal" },
    { value: "COMMERCIAL", label: "Commercial" },
    { value: "LIFE_ANNUITIES", label: "Life & Annuities" }
  ];
  protected selectedCategory: string = "";
  protected lobOptions: BusinessLineOption[] = [];
  protected selectedLobCodes: string[] = [];

  // Consumer follow-up automation — the toggle here blocks manual Preview/Send while enabled; the
  // other fields are saved together via the panel's own Save button.
  protected automationSettings: CampaignAutomationSettings | null = null;
  protected isLoadingAutomation: boolean = false;
  protected isSavingAutomation: boolean = false;
  protected automationCadenceMode: CampaignCadenceMode = "ANNUAL";
  protected automationYearsBack: number = 1;
  /** "HH:mm" for the native time input — seconds are added back when saving. */
  protected automationRunTime: string = "11:00";
  protected automationCategory: string = "";
  protected automationLobCodes: string[] = [];

  private readonly onDestroy$: Subject<void> = new Subject<void>();

  constructor(
    private consumerEmailCampaignService: ConsumerEmailCampaignService,
    // Category/LOB dropdown data is channel-agnostic — reused from the SMS campaign service rather
    // than duplicating the same lookup endpoint here.
    private consumerSmsCampaignService: ConsumerSmsCampaignService,
    private campaignAutomationSettingsService: CampaignAutomationSettingsService,
    private dialog: Dialog,
    private toastService: ToastService,
    private stateManagementService: StateManagementService
  ) {}

  ngOnInit(): void {
    this.stateManagementService.setPageTitle("Consumer Email Campaign | Momentum Edge");
    this.stateManagementService.pageHeading.set("Consumer Referral Email Campaign");
    this.loadLobOptions();
    this.loadAutomationSettings();
  }

  protected get isAutomationEnabled(): boolean {
    return !!this.automationSettings?.enabled;
  }

  /**
   * The Years Back input's up/down spinner respects min="1"/max="5", but typing a value directly
   * bypasses that entirely — ngModel just takes whatever was typed. Without this check, an
   * out-of-range value (e.g. 6) would sail through to the backend and fail there instead.
   */
  protected get yearsBackError(): string | null {
    if (this.automationYearsBack == null || `${this.automationYearsBack}`.trim() === "") {
      return "Years Back is required.";
    }
    if (
      !Number.isInteger(this.automationYearsBack) ||
      this.automationYearsBack < 1 ||
      this.automationYearsBack > 5
    ) {
      return "Years Back must be a whole number between 1 and 5.";
    }
    return null;
  }

  private loadAutomationSettings(): void {
    this.isLoadingAutomation = true;
    this.campaignAutomationSettingsService
      .getSettings("EMAIL")
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (settings: CampaignAutomationSettings): void => {
          this.applyAutomationSettings(settings);
          this.isLoadingAutomation = false;
        },
        error: (error: HttpErrorResponse): void => {
          this.isLoadingAutomation = false;
          this.toastService.open(
            this.extractErrorMessage(error, "Failed to load automation settings."),
            "Close",
            { duration: 7000, panelClass: ["bg-red-600"] }
          );
        }
      });
  }

  private applyAutomationSettings(settings: CampaignAutomationSettings): void {
    this.automationSettings = settings;
    this.automationCadenceMode = settings.cadenceMode;
    this.automationYearsBack = settings.yearsBack;
    this.automationRunTime = (settings.runTime || "11:00:00").substring(0, 5);
    this.automationCategory = settings.category ?? "";
    this.automationLobCodes = settings.lobCodes ?? [];
  }

  /** Toggling automation on/off saves immediately — same UX as the Feature Flags screen. */
  protected toggleAutomationEnabled(): void {
    this.saveAutomationSettings(!this.isAutomationEnabled);
  }

  protected saveAutomationSettings(enabledOverride?: boolean): void {
    if (this.yearsBackError) {
      this.toastService.open(this.yearsBackError, "Close", {
        duration: 7000,
        panelClass: ["bg-red-600"]
      });
      return;
    }

    const enabled: boolean = enabledOverride ?? this.isAutomationEnabled;
    this.isSavingAutomation = true;
    const request: CampaignAutomationSettingsRequest = {
      enabled,
      cadenceMode: this.automationCadenceMode,
      yearsBack: this.automationYearsBack,
      runTime: `${this.automationRunTime}:00`,
      category: this.automationCategory || undefined,
      lobCodes: this.automationLobCodes.length > 0 ? this.automationLobCodes : undefined
    };

    this.campaignAutomationSettingsService
      .updateSettings("EMAIL", request)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (settings: CampaignAutomationSettings): void => {
          this.applyAutomationSettings(settings);
          this.isSavingAutomation = false;
          this.toastService.open("Automation settings saved.", "Close", {
            duration: 5000,
            panelClass: ["bg-green-600"]
          });
        },
        error: (error: HttpErrorResponse): void => {
          this.isSavingAutomation = false;
          this.toastService.open(
            this.extractErrorMessage(error, "Failed to save automation settings."),
            "Close",
            { duration: 7000, panelClass: ["bg-red-600"] }
          );
        }
      });
  }

  private loadLobOptions(): void {
    this.consumerSmsCampaignService
      .getBusinessLines()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (options: BusinessLineOption[]): void => {
          this.lobOptions = [...options].sort((a, b) => a.name.localeCompare(b.name));
        },
        error: (error: HttpErrorResponse): void => {
          this.toastService.open(
            this.extractErrorMessage(error, "Failed to load line-of-business options."),
            "Close",
            { duration: 7000, panelClass: ["bg-red-600"] }
          );
        }
      });
  }

  protected get dateRangeError(): string | null {
    if (!this.fromDate || !this.toDate) {
      return null;
    }
    if (this.fromDate > this.maxDate || this.toDate > this.maxDate) {
      return "From Date and To Date cannot be in the future.";
    }
    if (this.fromDate > this.toDate) {
      return "From Date must be on or before To Date.";
    }
    return null;
  }

  protected preview(): void {
    this.isPreviewing = true;
    this.sendResult = null;
    const request: ConsumerEmailCampaignRequest = this.buildRequest(true);
    this.consumerEmailCampaignService
      .runCampaign(request)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (result: ConsumerEmailCampaignResult): void => {
          this.previewResult = result;
          this.isPreviewing = false;
        },
        error: (error: HttpErrorResponse): void => {
          this.isPreviewing = false;
          this.previewResult = null;
          this.toastService.open(
            this.extractErrorMessage(error, "Failed to preview the campaign."),
            "Close",
            { duration: 7000, panelClass: ["bg-red-600"] }
          );
        }
      });
  }

  protected confirmSend(): void {
    if (!this.previewResult) {
      return;
    }

    const data: ConfirmationModalData = {
      title: "Send Consumer Referral Email",
      message: `Are you sure you want to send this email to ${this.previewResult.eligibleCount} referral(s)? This cannot be undone.`,
      messageHtml: `Are you sure you want to send this email to <strong>${this.previewResult.eligibleCount}</strong> referral(s)? This cannot be undone.`,
      confirmButtonText: "Send",
      cancelButtonText: "Cancel"
    };

    this.dialog
      .open<boolean>(ConfirmationModalComponent, {
        panelClass: "centered-dialog",
        hasBackdrop: true,
        data
      })
      .closed.pipe(takeUntil(this.onDestroy$))
      .subscribe((confirmed: boolean | undefined): void => {
        if (confirmed) {
          this.executeSend();
        }
      });
  }

  private executeSend(): void {
    this.isSending = true;
    const request: ConsumerEmailCampaignRequest = this.buildRequest(false);
    this.consumerEmailCampaignService
      .runCampaign(request)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (result: ConsumerEmailCampaignResult): void => {
          this.isSending = false;
          this.sendResult = result;
          this.previewResult = null;
          this.toastService.open(
            `${result.queued} email(s) queued for sending. Delivery results will appear in the ` +
              `email notification logs shortly, not here.`,
            "Close",
            { duration: 9000, panelClass: ["bg-green-600"] }
          );
        },
        error: (error: HttpErrorResponse): void => {
          this.isSending = false;
          this.toastService.open(
            this.extractErrorMessage(error, "Failed to send the campaign."),
            "Close",
            { duration: 7000, panelClass: ["bg-red-600"] }
          );
        }
      });
  }

  private extractErrorMessage(error: HttpErrorResponse, fallback: string): string {
    if (error.error && typeof error.error === "object" && error.error.message) {
      return error.error.message;
    }
    if (typeof error.error === "string" && error.error.trim()) {
      return error.error;
    }
    if (error.message) {
      return error.message;
    }
    return fallback;
  }

  private buildRequest(dryRun: boolean): ConsumerEmailCampaignRequest {
    const request: ConsumerEmailCampaignRequest = {
      fromDate: this.fromDate,
      toDate: this.toDate,
      dryRun
    };
    if (this.limit != null && this.limit > 0) {
      request.limit = this.limit;
    }
    if (this.selectedCategory) {
      request.category = this.selectedCategory;
    }
    if (this.selectedLobCodes.length > 0) {
      request.lobCodes = this.selectedLobCodes;
    }
    return request;
  }

  private today(): string {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${now.getFullYear()}-${month}-${day}`;
  }

  ngOnDestroy(): void {
    this.stateManagementService.resetHeadingAndTitle();
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
